/* casam-landing-promo.js — DRM-280 Path 2: data-driven Shogun landing-page URL pointer.
   The spine writes a { "/pages/x": "CODE", ... } map to shop metafield
   custom.landing_promo_pages when a landing-page promo is approved (cart_promos.wire_landing_pages).
   This applies the mapped discount code on that page only — zero theme deploy to add/remove a page.

   Generalizes the original hardcoded casam-seasoning-b2g1.js. No hardcoded paths, no date window
   here — the discount code's OWN Shopify validity window governs whether /discount/CODE sticks, and
   the spine unwires a page from the map when its campaign ends. Same anti-stacking + idempotency
   guards as casam-fday30.js. External asset, no inline HTML (geoblock P0 lesson). */
(function () {
  "use strict";

  // ---- pure helpers (also exported for node tests) ----

  // Never clobber a code the shopper already holds (anti-stacking: first code wins).
  //
  // Keys on code PRESENCE and deliberately IGNORES Shopify's `applicable` flag — the same
  // model as promoLiveForCart() in casam-promos.js, which carries the full reasoning. Short
  // version, measured on the live store with promo-eligible blends and the code held:
  //     2 blends (threshold unmet) → {code:"SEASONING", applicable:false}
  //     3 blends (threshold met)   → {code:"SEASONING", applicable:true}, line discount 1699
  // `applicable` means "is the threshold met right now", NOT "does the shopper hold the code".
  //
  // So a threshold-gated code reads false until its threshold is met, and gating on it made
  // this engine replace exactly the class of code it promotes: a shopper landing on a wired
  // page while holding another B2G1 below its threshold read as "no code", and /discount/CODE
  // silently took the offer they arrived with (a Shopify cart holds one code).
  function cartHasDiscount(cart) {
    var codes = (cart && cart.discount_codes) || [];
    if (codes.some(function (d) { return d && d.code; })) return true;
    // Automatic (non-code) discounts are not a clobber risk — they're independent of the cart's
    // one code slot. Kept as a conservative do-not-stack guard: if an automatic is already
    // paying out, don't layer a code whose combinesWith rules aren't visible from here.
    return (((cart && cart.cart_level_discount_applications) || []).length) > 0;
  }

  // ---- node export for tests ----
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { cartHasDiscount: cartHasDiscount };
    return;
  }

  // ---- browser runtime ----
  var MAP = window.CASAM_LANDING_PROMOS || {};
  if (!MAP || typeof MAP !== "object") return;

  function path() {
    var p = (location.pathname || "").toLowerCase();
    if (p.length > 1 && p.charAt(p.length - 1) === "/") p = p.slice(0, -1);
    return p;
  }
  var CODE = MAP[path()];
  if (!CODE) return;                                  // this page isn't wired — do nothing

  var SS_KEY = "casam_landing_promo_applied:" + CODE;

  function getCart() { return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }); }
  function applied() { try { return sessionStorage.getItem(SS_KEY) === "1"; } catch (e) { return false; } }
  function markApplied() { try { sessionStorage.setItem(SS_KEY, "1"); } catch (e) {} }

  // The sessionStorage guard must record SUCCESS, not intent. Marking before the fetch
  // resolved meant a single failed /discount (network blip, adblock, 5xx) burned the guard
  // for the whole session: the code never landed, .catch swallowed it, and post-DRM-527 the
  // nudge gates on code presence — so the shopper silently got no offer AND no message about
  // it. Mark only on a response we actually saw.
  //
  // Attempts are bounded per page load (and cart events are debounced) so a persistently
  // failing endpoint can't be hammered on every cart event. A 302 to a page we can't read
  // still counts as success: /discount/CODE redirects whether or not the code was valid, so
  // r.ok means "Shopify answered", and an expired code is not worth retrying anyway.
  var MAX_ATTEMPTS = 3;
  var inflight = false, attempts = 0;

  function tryApply() {
    if (applied() || inflight || attempts >= MAX_ATTEMPTS) return;
    getCart().then(function (cart) {
      if (applied() || inflight) return;                    // a concurrent pass got there first
      if (cartHasDiscount(cart)) { markApplied(); return; } // shopper's code wins — never replace it
      inflight = true; attempts++;
      fetch("/discount/" + encodeURIComponent(CODE), { credentials: "same-origin" })
        .then(function (r) {
          inflight = false;
          if (!r || !r.ok) return;                          // guard NOT burned — a later cart event retries
          markApplied();
          document.dispatchEvent(new CustomEvent("cart:build"));
          document.dispatchEvent(new CustomEvent("cart:updated"));
        })
        .catch(function () { inflight = false; });
    }).catch(function () {});
  }

  var t = null;
  function debounced() { clearTimeout(t); t = setTimeout(tryApply, 400); }
  ["cart:updated", "cart:build", "cart:open", "ajaxProduct:added"].forEach(function (ev) {
    document.addEventListener(ev, debounced);
  });
  if (document.readyState !== "loading") tryApply(); else document.addEventListener("DOMContentLoaded", tryApply);
})();
